import { styled } from "goober";
import {
  type ReactNode,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Tooltip } from "../Tooltip";
import { rowsForBadgeWidths } from "./MembersSettings/lib/badgeOverflow";
import { PillBadge } from "./shared";

const Root = styled("div")`
  position: relative;
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
  width: 100%;
  max-width: 100%;
  max-height: 20px;
  overflow: hidden;
  min-width: 0;
`;

const MeasureLayer = styled("div")`
  position: absolute;
  left: 0;
  top: 0;
  visibility: hidden;
  pointer-events: none;
  display: flex;
  flex-wrap: nowrap;
  gap: 4px;
  align-items: center;
  white-space: nowrap;
`;

const MeasureItem = styled("span")`
  display: inline-flex;
`;

export function OverflowBadges<T>({
  items,
  keyFn,
  renderBadge,
  overflowTooltip,
  extraOverflowCount = 0,
  extraOverflowLabel,
}: {
  items: readonly T[];
  keyFn: (item: T) => string;
  renderBadge: (item: T) => ReactNode;
  overflowTooltip: (hiddenItems: readonly T[]) => string;
  extraOverflowCount?: number;
  extraOverflowLabel?: string;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(items.length);

  const computeVisibleCount = useCallback(() => {
    const root = rootRef.current;
    const measure = measureRef.current;
    if (!root || !measure) return;

    const containerWidth = root.clientWidth;
    if (containerWidth === 0) return;

    const badgeWidths = Array.from(
      measure.querySelectorAll<HTMLElement>("[data-overflow-badge]")
    ).map((el) => el.offsetWidth);
    const countWidths = new Map(
      Array.from(
        measure.querySelectorAll<HTMLElement>("[data-overflow-count]")
      ).map((el) => [Number(el.dataset.overflowCount), el.offsetWidth])
    );

    let next = items.length;
    for (let count = items.length; count >= 0; count--) {
      const hidden = items.length - count;
      const total = hidden + extraOverflowCount;
      const widths =
        total > 0
          ? [...badgeWidths.slice(0, count), countWidths.get(total) ?? 0]
          : badgeWidths;
      if (rowsForBadgeWidths(widths, containerWidth) <= 1) {
        next = count;
        break;
      }
    }

    setVisibleCount((current) => (current === next ? current : next));
  }, [items, extraOverflowCount]);

  useLayoutEffect(() => {
    computeVisibleCount();
    const root = rootRef.current;
    if (!root) return;
    const observer = new ResizeObserver(computeVisibleCount);
    observer.observe(root);
    return () => observer.disconnect();
  }, [computeVisibleCount]);

  const visible = items.slice(0, visibleCount);
  const hidden = items.slice(visibleCount);
  const totalOverflow = hidden.length + extraOverflowCount;

  return (
    <Root ref={rootRef}>
      {visible.map((item) => (
        <span key={keyFn(item)}>{renderBadge(item)}</span>
      ))}
      {totalOverflow > 0 && (
        <Tooltip
          content={[
            hidden.length > 0 ? overflowTooltip(hidden) : "",
            extraOverflowLabel ?? "",
          ]
            .filter(Boolean)
            .join(", ")}
        >
          <PillBadge>+{totalOverflow}</PillBadge>
        </Tooltip>
      )}
      <MeasureLayer ref={measureRef}>
        {items.map((item) => (
          <MeasureItem key={keyFn(item)} data-overflow-badge>
            {renderBadge(item)}
          </MeasureItem>
        ))}
        {Array.from({ length: items.length + extraOverflowCount }, (_, i) => {
          const n = i + 1;
          return (
            <MeasureItem key={`count-${n}`} data-overflow-count={n}>
              <PillBadge>+{n}</PillBadge>
            </MeasureItem>
          );
        })}
      </MeasureLayer>
    </Root>
  );
}
